/**
 * Design tokens for the app theme.
 * Keep all semantic colors here so the theme and components can reuse them consistently.
 */
const colors = {
  primaryMain: '#5bbfdd',
  secondaryMain: '#333F50',
  white: '#ffffff',
  surface: '#ffffff',
  surfaceAlt: '#f1f5f9',
  title_text: '#333333',
  subtext: '#333F50',
  successMain: '#22c55e',
  warningMain: '#f59e0b',
  errorMain: '#ef4444',
  panelBackground: '#f8fafc',
  textDisabled: '#333F50',
  borderColor: '#e6e6e6',
  // Badge colors: Bronze / Silver / Gold
  badgeBronze: '#BF9E81',
  badgeSilver: '#C0C0C0',
  badgeGold: '#F0CA4D',
};

// MUI palette configuration using semantic color tokens.
const palette = {
  mode: 'light',
  primary: {
    main: colors.primaryMain,
    light: colors.primaryMain,
    dark: colors.primaryMain,
    contrastText: colors.white,
  },
  secondary: {
    main: colors.secondaryMain,
    light: colors.secondaryMain,
    dark: colors.secondaryMain,
    contrastText: '#0f172a',
  },
  success: {
    main: colors.successMain,
    contrastText: '#0f172a',
  },
  warning: {
    main: colors.warningMain,
    contrastText: '#0f172a',
  },
  error: {
    main: colors.errorMain,
    dark: '#c62828',
    contrastText: '#0f172a',
  },
  background: {
    default: colors.panelBackground,
    paper: colors.surface,
  },
  text: {
    primary: colors.title_text,
    secondary: colors.textDisabled,
    disabled: colors.textDisabled,
  },
  divider: colors.subtext,
};

// Typography settings for headings, body text and buttons.
const typography = {
  fontFamily: '"Montserrat", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 300, color: colors.secondaryMain },
  h2: { fontWeight: 300, color: colors.secondaryMain },
  h3: { fontWeight: 300, color: colors.secondaryMain },
  h4: { fontWeight: 300, color: colors.secondaryMain },
  h5: { fontWeight: 300, color: colors.secondaryMain },
  h6: { fontWeight: 300, color: colors.secondaryMain },
  body1: { color: colors.textDisabled, fontWeight: 300 },
  body2: { color: colors.textDisabled, fontWeight: 300 },
  button: { textTransform: 'none', fontWeight: 300, letterSpacing: '0.02em' },
};

// Shape tokens used by MUI components for consistent rounding.
const shape = { borderRadius: 0 };

// Component-level style overrides and customizations.
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: colors.panelBackground,
        color: colors.title_text,
        fontWeight: 300,
      },
      a: {
        color: colors.primaryMain,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        fontWeight: 300,
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: 0,
        padding: '12px 28px',
        textTransform: 'none',
        boxShadow: 'none',
        fontWeight: 300,
        transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
      },
      containedPrimary: {
        backgroundColor: colors.primaryMain,
        color: colors.secondaryMain,
        fontWeight: 500,
        boxShadow: '0 16px 30px rgba(91, 191, 221, 0.18)',
        '&:hover': {
          backgroundColor: colors.primaryMain,
          boxShadow: '0 18px 36px rgba(91, 191, 221, 0.22)',
        },
        '&[type="submit"]': {
          color: colors.white,
        },
      },
      outlinedPrimary: {
        borderColor: colors.borderColor,
        color: colors.title_text,
        '&:hover': {
          borderColor: colors.primaryMain,
          backgroundColor: 'rgba(91, 191, 221, 0.08)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        boxShadow: '0 16px 45px rgba(15, 23, 42, 0.18)',
        borderRadius: 0,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: colors.white,
        borderRadius: 0,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: colors.white,
        color: colors.title_text,
        borderRadius: 0,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: colors.title_text,
        '&.Mui-selected': {
          backgroundColor: 'rgba(91, 191, 221, 0.08)',
        },
        '&:hover': {
          backgroundColor: 'rgba(148, 163, 184, 0.08)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: colors.surface,
        color: colors.title_text,
        borderRadius: 0,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.borderColor,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: colors.primaryMain,
        },
      },
      input: {
        color: colors.title_text,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: colors.textDisabled,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        backgroundColor: 'rgba(148, 163, 184, 0.08)',
        color: colors.textDisabled,
        borderRadius: 0,
      },
      outlined: {
        borderColor: 'rgba(148, 163, 184, 0.22)',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: colors.primaryMain,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
};

// Theme export consumed by MUI ThemeProvider in the app.
const themeConfig = {
  colors,
  palette,
  typography,
  shape,
  components,
};

export default themeConfig;
